// The canvas space (05e-1) — main.js's grid size, placement checks and the machinery
// that grows / shrinks / shifts the canvas around a drag, moved here. The sums are
// placement.ts; this file reads the live page and applies them.
//
// Module boxes are read off the elements (style.left / top / width / height): during a
// gesture the page is the truth, and a drag moves every module by DRAG_EXPANSION while it
// holds the canvas open (05e's fork 2A — the store gets the boxes when the gesture ends,
// from 05e-2). The workspace shift is the store's.


import { useProtoStore } from "../store/store.js";
import { moduleElementOf } from "./module-registry.js";
import { welcomeRect } from "./welcome.js";
import { getScale as scale } from "./zoom.js";
import {
  DRAG_EXPANSION,
  clampGridRows,
  dropShift,
  firstEmptySlot,
  firstOverlap,
  footprintOf,
  initialGridCols,
  isAreaClear as isAreaClearPure,
  minGridRows,
  paddedGrid,
  snapCoords,
  topLeftCluster,
  trimAmount,
  type Footprint,
} from "./placement.js";

// ── State (main.js's GRID_COLS, MIN_GRID_ROWS, workspaceGridRows, the drag expansion) ──
let workspace: HTMLElement | null = null;
let gridCols = 0;
let minRows = 0;
let gridRows = 0;
let expanded = false;
// Where the page was scrolled when the drag opened the canvas, and right after.
let preExpansionScrollX = 0;
let preExpansionScrollY = 0;
let expansionScrollX = 0;
let expansionScrollY = 0;

function ws(): HTMLElement {
  if (!workspace) throw new Error("installCanvasSpace() hasn't run");
  return workspace;
}

function applyGridWidth(): void {
  ws().style.width = `calc(${gridCols} * (var(--grid-size) + var(--grid-gap)))`;
}

function placedModules(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(".placed-module"));
}

// `|| "0"` as main.js's parseInt(style.left || 0): a module not yet positioned sits at 0.
const leftOf = (m: HTMLElement) => Number.parseInt(m.style.left || "0", 10);
const topOf = (m: HTMLElement) => Number.parseInt(m.style.top || "0", 10);

function shiftAll(dx: number, dy: number): void {
  for (const m of placedModules()) {
    m.style.left = `${leftOf(m) + dx}px`;
    m.style.top = `${topOf(m) + dy}px`;
  }
}

/** Size the canvas to the viewport (main.js's load-time setup). Before main.js loads. */
export function installCanvasSpace(el: HTMLElement): void {
  workspace = el;
  gridCols = initialGridCols(window.innerWidth);
  minRows = minGridRows(window.innerHeight);
  gridRows = minRows;
  applyGridWidth();
  setWorkspaceGridRows(minRows);
}

export function setWorkspaceGridRows(rowCount: number): void {
  gridRows = clampGridRows(rowCount, minRows);
  ws().style.height = `calc(${gridRows} * (var(--grid-size) + var(--grid-gap)))`;
}

export function resetWorkspaceGridRows(): void {
  setWorkspaceGridRows(minRows);
}

export const getGridCols = (): number => gridCols;
export const getGridRows = (): number => gridRows;
export const isDragExpanded = (): boolean => expanded;

export const getSnapCoords = snapCoords;

/** A placed module's grid footprint, from its live inline styles. */
export function moduleFootprint(el: HTMLElement): Footprint {
  return footprintOf(
    leftOf(el),
    topOf(el),
    Number.parseInt(el.style.width, 10),
    Number.parseInt(el.style.height, 10),
  );
}

/**
 * main.js isAreaClear, over the live page. `ignore` is the module being placed, or a Set
 * (a group drag — its members move together, so they can't block each other). A Notes
 * module may land on anything: read off `ignore` itself, or `forceNotesExempt` when
 * there's no single element to read (a Notes tile still being dragged in, a group).
 */
export function isAreaClear(
  col: number,
  row: number,
  cols: number,
  rows: number,
  ignore: HTMLElement | Set<HTMLElement> | null = null,
  forceNotesExempt = false,
): boolean {
  const ignored = (m: HTMLElement) => (ignore instanceof Set ? ignore.has(m) : m === ignore);
  const notesExempt =
    forceNotesExempt || (!!ignore && !(ignore instanceof Set) && ignore.classList.contains("module-notes"));
  const others = placedModules().filter((m) => !ignored(m)).map(moduleFootprint);
  return isAreaClearPure({ col, row, cols, rows }, others, { gridCols, gridRows, expanded }, notesExempt);
}

/** main.js findOverlappingModule: the module in the way, or null. */
export function findOverlappingModule(
  col: number,
  row: number,
  cols: number,
  rows: number,
  ignore: HTMLElement | ReadonlySet<HTMLElement> | null = null,
): HTMLElement | null {
  // A set: a whole group being moved (its members never count as what it lands on).
  const ignored = (m: HTMLElement) => (ignore instanceof Set ? ignore.has(m) : m === ignore);
  const mods = placedModules().filter((m) => !ignored(m));
  const i = firstOverlap({ col, row, cols, rows }, mods.map(moduleFootprint));
  return i < 0 ? null : mods[i]!;
}

/** main.js getFirstEmptySlot (a side-panel tile click). Grows the canvas's rows when it
 *  has to, as main.js did — also when a later pass then finds a spot. */
export function getFirstEmptySlot(cols: number, rows: number): { col: number; row: number } {
  const slot = firstEmptySlot(cols, rows, {
    gridCols,
    gridRows,
    minRows,
    // Rows added by an earlier pass count for the next one (main.js read the live value).
    isClear: (t, rowsNow) => {
      if (rowsNow !== gridRows) setWorkspaceGridRows(rowsNow);
      return isAreaClear(t.col, t.row, t.cols, t.rows, null, false);
    },
    welcome: window.innerWidth <= 768 ? null : welcomeRect(),
  });
  if (slot.gridRows !== gridRows) setWorkspaceGridRows(slot.gridRows);
  return { col: slot.col, row: slot.row };
}

/**
 * A drag starts: open the canvas DRAG_EXPANSION px in every direction so the module can
 * go past the edges — every module moves right / down by that much and the page scrolls
 * with them, so nothing seems to move.
 */
export function expandWorkspace(): void {
  if (expanded) return;
  expanded = true;
  preExpansionScrollX = window.scrollX;
  preExpansionScrollY = window.scrollY;

  const el = ws();
  const W = el.offsetWidth;
  const H = el.offsetHeight;
  const s = scale();
  // Chrome sizes the scroll area from the zoomed (transformed) box. Zoomed out, that can
  // be smaller than the window, and the scroll below would be cut short — so pad the
  // canvas enough for it to land. Modules still move by DRAG_EXPANSION only.
  const deficitX = Math.max(0, window.innerWidth - 56 - W * s);
  const deficitY = Math.max(0, window.innerHeight - 66 - H * s);
  el.style.width = `${W + DRAG_EXPANSION + Math.ceil(deficitX / s)}px`;
  el.style.height = `${H + DRAG_EXPANSION + Math.ceil(deficitY / s)}px`;

  shiftAll(DRAG_EXPANSION, DRAG_EXPANSION);
  window.scrollTo(preExpansionScrollX + DRAG_EXPANSION * s, preExpansionScrollY + DRAG_EXPANSION * s);
  expansionScrollX = window.scrollX;
  expansionScrollY = window.scrollY;
}

/**
 * The drag ended: close the canvas again. A drop left of / above the origin shifts every
 * module to make room (the workspace shift, kept in the store for undo); a cancel restores
 * everything, scroll included.
 *
 * `keepView` (with isCancel): a drop that acted on an EXISTING module — a merge, a switch,
 * a Similarity overlay. Nothing new is placed, so no shift; but the view stays where the
 * drag took you (on the target), like a placed drop — not back where the drag started
 * (Ben, 2026-10-02: a merge 20 squares down used to scroll the page back up).
 */
export function collapseWorkspace(snapX: number, snapY: number, isCancel: boolean, keepView = false): void {
  if (!expanded) return;
  expanded = false;
  const shift = dropShift(snapX, snapY, isCancel);
  // Read the scroll BEFORE the canvas shrinks: shrinking makes Chrome clamp it at once.
  const scrollXNow = window.scrollX;
  const scrollYNow = window.scrollY;

  shiftAll(-DRAG_EXPANSION + shift.x, -DRAG_EXPANSION + shift.y);
  applyGridWidth();
  setWorkspaceGridRows(gridRows);
  if (isCancel && keepView) {
    window.scrollTo(preExpansionScrollX + (scrollXNow - expansionScrollX), preExpansionScrollY + (scrollYNow - expansionScrollY));
  } else if (isCancel) {
    window.scrollTo(preExpansionScrollX, preExpansionScrollY);
  } else {
    const store = useProtoStore.getState();
    store.setWorkspaceShift(store.workspaceShiftX + shift.x, store.workspaceShiftY + shift.y);
    // The view stays where it was before the drag (adding the shift made the page jump
    // so the dropped module sat off-screen to the left).
    window.scrollTo(preExpansionScrollX + (scrollXNow - expansionScrollX), preExpansionScrollY + (scrollYNow - expansionScrollY));
    trimWorkspaceShift();
    ensureCanvasPadding();
  }
  // Every drag that opened the canvas ends here (a move, a side-panel drop, a saved
  // dashboard), so the store gets the boxes here once — none of main.js's drop branches
  // can miss it (05e-2's fork 2A).
  commitBoxes();
}

function baseSize(el: HTMLElement, attr: "data-base-cols" | "data-base-rows"): number {
  const n = Number.parseInt(el.getAttribute(attr) ?? "", 10);
  if (Number.isNaN(n)) throw new Error(`commitBoxes: ${attr} missing on a .placed-module (${el.className})`);
  return n;
}

/** Copy every module's box off the page into the store (a drag's end: the page is the
 *  truth during it). A resize doesn't need it — it writes the store itself (05f). */
/** Close the canvas after a drop onto an existing module (merge / switch / overlay): no
 *  shift, and the view stays on the target (see collapseWorkspace's `keepView`). */
export function collapseWorkspaceOnTarget(): void {
  collapseWorkspace(0, 0, true, true);
}

export function commitBoxes(): void {
  const store = useProtoStore.getState();
  const boxes = store.modules.flatMap((m) => {
    const el = moduleElementOf(m.id);
    if (!el) return [];
    return [
      {
        id: m.id,
        x: leftOf(el),
        y: topOf(el),
        cols: baseSize(el, "data-base-cols"),
        rows: baseSize(el, "data-base-rows"),
      },
    ];
  });
  store.commitBoxes(boxes);
}

/** Take the built-up shift back off once the space it made (left of / above the modules)
 *  is empty again. */
export function trimWorkspaceShift(): void {
  const store = useProtoStore.getState();
  const shift = { x: store.workspaceShiftX, y: store.workspaceShiftY };
  if (shift.x === 0 && shift.y === 0) return;
  const mods = placedModules();
  if (!mods.length) return;
  const trim = trimAmount(shift, Math.min(...mods.map(leftOf)), Math.min(...mods.map(topOf)));
  if (trim.x > 0 || trim.y > 0) {
    shiftAll(-trim.x, -trim.y);
    store.setWorkspaceShift(shift.x - trim.x, shift.y - trim.y);
    const s = scale();
    window.scrollBy(-trim.x * s, -trim.y * s);
  }
}

/**
 * Keep EDGE_PADDING squares of real canvas right of / below the cluster, so scrolling to
 * the last module finds empty grid beyond it. Only grows. Does nothing mid-drag: the
 * modules are still moved out by DRAG_EXPANSION, which would read as a huge cluster
 * (collapseWorkspace calls it once they're back).
 */
export function ensureCanvasPadding(): void {
  if (expanded) return;
  const mods = placedModules();
  if (!mods.length) return;
  const next = paddedGrid(mods.map(moduleFootprint), gridCols, gridRows);
  if (next.gridCols !== gridCols) {
    gridCols = next.gridCols;
    applyGridWidth();
  }
  if (next.gridRows !== gridRows) setWorkspaceGridRows(next.gridRows);
}

/** Scroll so the cluster's top-left sits just inside the top bar and the side rail (a
 *  preset / dashboard load). */
export function scrollToTopLeft(): void {
  const mods = placedModules();
  if (!mods.length) return;
  const picked = topLeftCluster(mods.map((m) => ({ left: leftOf(m), top: topOf(m) })));
  // Screen rects, so the canvas margin and the zoom are already counted.
  const rects = picked.map((i) => mods[i]!.getBoundingClientRect());
  const minLeft = Math.min(...rects.map((r) => r.left));
  const minTop = Math.min(...rects.map((r) => r.top));
  const PAD_TOP = 60; // the 50px top bar + a little
  const PAD_LEFT = 66; // the 56px side rail + a little
  window.scrollTo({
    left: Math.max(0, window.scrollX + minLeft - PAD_LEFT),
    top: Math.max(0, window.scrollY + minTop - PAD_TOP),
    behavior: "instant",
  });
}

/** The canvas-space calls, part of the shell's api (shell-actions.ts). */
export const canvasSpaceApi = {
  getGridCols,
  getGridRows,
  isDragExpanded,
  setWorkspaceGridRows,
  resetWorkspaceGridRows,
  getSnapCoords,
  moduleFootprint,
  isAreaClear,
  findOverlappingModule,
  getFirstEmptySlot,
  expandWorkspace,
  collapseWorkspace,
  ensureCanvasPadding,
  scrollToTopLeft,
};
export type CanvasSpaceApi = typeof canvasSpaceApi;
